import { logger } from '../../Util';
import DataState from '../../Types/DataState';
import { makeMutation, makeQuery } from '../AppSyncClient';
import * as trainingQueries from './queries';
import * as trainingMutations from './mutations';
import {
  TrainingStatsResult,
  TrainingCatalogResult,
  TrainingAssignmentInput,
  AssignTrainingResponse,
} from './schema';
import { Profile } from '../../Types/Profile';
import { ExtendedUser } from '../../Types';
import { createUserInput } from '../Utils/helper';

export interface GetTrainingStatsInput {
  userID: string;
  users: string[];
  startDate: string;
  endDate: string;
}

export interface GetTrainingCatalogVariables {
  limit?: number;
  nextToken?: string;
}

export interface GetTrainingExperienceDataInput {
  userId: string;
  userAlias: string;
}

export interface UserInput {
  userId: string;
  userAlias: string;
}

export interface GetAssignedTrainingsVaribles {
  input: {
    users: UserInput[];
  };
}

/**
 * Fetches training statistics for a user and their direct reports
 */
const getTrainingStats = async (
  variables: GetTrainingStatsInput,
  setTrainingStats: Function,
  setTrainingStatsState: Function
): Promise<void> => {
  logger.info('getTrainingStats', variables);
  setTrainingStatsState(DataState.LOADING);
  try {
    const {
      data: { getTrainingStats },
    }: { data: { getTrainingStats: TrainingStatsResult } } = await makeQuery({
      query: trainingQueries.getTrainingStatsQuery,
      variables,
    });

    setTrainingStats(getTrainingStats);
    setTrainingStatsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving training stats data',
      e as Error,
      variables
    );
    setTrainingStatsState(DataState.ERROR);
  }
};

/**
 * Fetches the training catalog
 */
const getTrainingCatalog = async (
  variables: GetTrainingCatalogVariables,
  setTrainingCatalog: Function,
  setTrainingCatalogState: Function
): Promise<void> => {
  logger.info('getTrainingCatalog', variables);
  setTrainingCatalogState(DataState.LOADING);
  try {
    const {
      data: { getTrainingCatalog },
    }: { data: { getTrainingCatalog: TrainingCatalogResult } } =
      await makeQuery({
        query: trainingQueries.getTrainingCatalogQuery,
        // Note: Currently trainingQueries.getTrainingCatalogQuery only handle with empty input
        // If we need use limit and token, please create a new graphQL query
        variables: variables,
      });

    setTrainingCatalog(getTrainingCatalog);
    setTrainingCatalogState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving training catalog data',
      e as Error,
      variables
    );
    setTrainingCatalogState(DataState.ERROR);
  }
};

/**
 * Assigns training to users
 */
const assignTraining = async (
  variables: TrainingAssignmentInput,
  setAssignmentResult: Function,
  setAssignmentResultState: Function
): Promise<void> => {
  logger.info('assignTraining', variables);
  setAssignmentResultState(DataState.LOADING);
  try {
    const {
      data: { assignTraining },
    }: { data: { assignTraining: AssignTrainingResponse } } =
      await makeMutation({
        mutation: trainingMutations.assignTraining,
        variables,
      });

    setAssignmentResult(assignTraining);
    setAssignmentResultState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in updateing training assignment data',
      e as Error,
      variables
    );
    setAssignmentResultState(DataState.ERROR);
  }
};

const prepareGetAssignedTrainingQueryForTM = (
  profile: Profile,
  cscEntityPrefix: string
): GetAssignedTrainingsVaribles => {
  const allUsers = [...profile.directReports, ...profile.sharedProfileCoachees];
  const userMap = new Map<string, ExtendedUser>();

  // Use Map to automatically handle uniqueness by userID
  allUsers.forEach(user => userMap.set(user.userID, user));

  const users: UserInput[] = Array.from(userMap.values()).map(user =>
    createUserInput(user, cscEntityPrefix)
  );

  return {
    input: { users },
  };
};

const prepareGetAssignedTrainingQueryForCSA = (
  profile: Profile,
  cscEntityPrefix: string
): GetAssignedTrainingsVaribles => {
  return {
    input: {
      users: [createUserInput(profile.user, cscEntityPrefix)],
    },
  };
};

// Export all training API functions
export default {
  getTrainingStats,
  getTrainingCatalog,
  assignTraining,
  prepareGetAssignedTrainingQueryForTM,
  prepareGetAssignedTrainingQueryForCSA,
};

// Export individual functions for named imports
export {
  getTrainingStats,
  getTrainingCatalog,
  assignTraining,
  prepareGetAssignedTrainingQueryForTM,
  prepareGetAssignedTrainingQueryForCSA,
};
